import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  type ScopeGroupType,
} from '@/types/quotation';
import { settingsService } from '@/services/settingsService';
import { industryService, type IndustryWithProjectTypes } from '@/services/industryService';
import { tenantService } from '@/services/tenantService';
import { bdmService } from '@/services/bdmService';
import { supabase } from '@/lib/supabase';
import { validateQuoteCreation } from '@/schemas/quoteSchema';
import { getScopeStepCopy } from '@/data/industryOptions';
import {
  FilePlus,
  User,
  Users,
  Home,
  Building2,
  Check,
  ChevronsUpDown,
  Plus,
  Minus,
  ChevronRight,
  ChevronLeft,
  Layout,
  ClipboardCheck,
  Sparkles
} from 'lucide-react';
import { useClientStore } from '@/stores/clientStore';
import { useDesignConsultantStore } from '@/stores/designConsultantStore';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { normalizeTextValue } from '@/utils/textNormalization';

// Backward-compat alias
type RoomCategory = ScopeGroupType;

interface NewQuoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateQuote: (
    quoteId: string,
    designConsultantId: string
  ) => Promise<void>;
}

type WizardStep = 1 | 2 | 3 | 4;

export function NewQuoteDialog({
  open,
  onOpenChange,
  onCreateQuote,
}: NewQuoteDialogProps) {
  const { clients, fetchClients, addClient } = useClientStore();
  const { designConsultants, fetchDesignConsultants } = useDesignConsultantStore();
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [isSaving, setIsSaving] = useState(false);
  const [openClientCombo, setOpenClientCombo] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [isContextReady, setIsContextReady] = useState(false);

  const [isAddingConsultant, setIsAddingConsultant] = useState(false);
  const [newConsultantForm, setNewConsultantForm] = useState({ name: '', email: '', phone: '' });
  const [isSavingConsultant, setIsSavingConsultant] = useState(false);

  const handleCreateNewConsultantInline = async () => {
    if (!newConsultantForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setIsSavingConsultant(true);
    try {
      const newBdm = await bdmService.addBdm({
        name: newConsultantForm.name,
        email: newConsultantForm.email || undefined,
        phone: newConsultantForm.phone || undefined,
      });
      await fetchDesignConsultants();
      setDesignConsultantId(newBdm.id);
      setIsAddingConsultant(false);
      setNewConsultantForm({ name: '', email: '', phone: '' });
      toast.success('Team member registered successfully');
    } catch (error) {
      console.error('Failed to create team member:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to register team member');
    } finally {
      setIsSavingConsultant(false);
    }
  };

  // Industry data — loaded from DB via industryService
  const [industries, setIndustries] = useState<IndustryWithProjectTypes[]>([]);
  const [selectedIndustryId, setSelectedIndustryId] = useState<string>('');
  const [selectedProjectTypeId, setSelectedProjectTypeId] = useState<string>('');
  const [scopeGroupTypes, setScopeGroupTypes] = useState<
    { value: string; label: string; allowMultiple: boolean }[]
  >([]);

  const applyDefaultIndustrySelection = useCallback(async (inds: IndustryWithProjectTypes[]) => {
    if (inds.length === 0) return;

    try {
      const tenant = await tenantService.getCurrentTenant();
      const primaryIndustry = tenant?.primary_industry_slug
        ? inds.find((industry) => industry.slug === tenant.primary_industry_slug)
        : null;
      const nextIndustry = primaryIndustry ?? (inds.length === 1 ? inds[0] : null);

      if (nextIndustry) {
        setSelectedIndustryId(nextIndustry.id);
        setSelectedProjectTypeId(nextIndustry.project_types[0]?.id ?? '');
      }
    } catch {
      if (inds.length === 1) {
        setSelectedIndustryId(inds[0].id);
        setSelectedProjectTypeId(inds[0].project_types[0]?.id ?? '');
      }
    }
  }, []);

  // Load industries once on mount, then auto-select tenant's primary industry
  useEffect(() => {
    industryService.getIndustries()
      .then(async (inds) => {
        setIndustries(inds);
        await applyDefaultIndustrySelection(inds);
        // Auto-select the tenant's primary industry to skip redundant step 1
        try {
          const tenant = await tenantService.getCurrentTenant();
          if (tenant?.primary_industry_slug) {
            const match = inds.find(i => i.slug === tenant.primary_industry_slug);
            if (match) {
              setSelectedIndustryId(match.id);
              // Filter to ONLY show primary industry to lock it
              setIndustries([match]);
            }
          }
        } catch {
          // Non-fatal — user can still pick industry manually
        }
      })
      .catch(() => {
        // Fallback: use settings-driven options if industry service unavailable
        setScopeGroupTypes(settingsService.getScopeGroupTypes());
      });
  }, []);

  // When industry changes, auto-select first project type and load its scope groups
  useEffect(() => {
    if (!selectedIndustryId) return;
    const industry = industries.find(i => i.id === selectedIndustryId);
    if (!industry) return;

    // Default to the first project type
    const defaultPt = industry.project_types[0];
    if (defaultPt) {
      setSelectedProjectTypeId(defaultPt.id);
      industryService.getProjectTypeWithTemplate(defaultPt.id).then(full => {
        if (full) {
          setScopeGroupTypes(
            full.scope_group_definitions.map(d => ({
              value:          d.value,
              label:          d.label,
              allowMultiple: d.allow_multiple,
            }))
          );
        }
      });
    }
  }, [selectedIndustryId, industries]);

  // Load initial store data
  useEffect(() => {
    Promise.all([
      fetchClients(),
      fetchDesignConsultants()
    ]).then(() => setIsContextReady(true));
  }, [fetchClients, fetchDesignConsultants]);

  // Auto-set the first consultant once designConsultants are loaded
  const [designConsultantId, setDesignConsultantId] = useState('');
  useEffect(() => {
    if (designConsultants.length > 0 && !designConsultantId) {
      setDesignConsultantId(designConsultants[0].id);
    }
  }, [designConsultants, designConsultantId]);

  // Form state
  const [clientName, setClientName] = useState('');
  const [projectName, setProjectName] = useState('');
  const [scopeCounts, setScopeCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (open) {
      fetchClients();
      fetchDesignConsultants();
      setClientName('');
      setProjectName('');
      setSelectedClientId('');
      setCurrentStep(1);
      setScopeCounts({});
      setIsContextReady(false);
      if (industries.length > 0) {
        void applyDefaultIndustrySelection(industries);
      } else {
        setSelectedIndustryId('');
        setSelectedProjectTypeId('');
      }
    }
  }, [open, fetchClients, fetchDesignConsultants, industries, applyDefaultIndustrySelection]);

  // Check if context is ready
  useEffect(() => {
    const checkContext = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const tenant = await tenantService.getCurrentTenant();
        setIsContextReady(!!user?.id && !!tenant?.id);
      } catch {
        setIsContextReady(false);
      }
    };
    
    if (open) {
      checkContext();
    }
  }, [open]);

  // Manage manual client additions or selection
  const handleClientSelect = (clientId: string) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setClientName(client.name);
      setProjectName(client.projectName || '');
      setSelectedClientId(clientId);
      if (client.bdmId) {
        setDesignConsultantId(client.bdmId);
      }
    }
    setOpenClientCombo(false);
  };

  const handleCreateNewClientInline = async () => {
    if (!clientName.trim()) return null;
    try {
      const newClient = await addClient({
        name: clientName,
        projectName: projectName || undefined,
        bdmId: designConsultantId || undefined,
      });
      setSelectedClientId(newClient.id);
      return newClient.id;
    } catch (err: unknown) {
      console.error('[NewQuoteDialog] Failed to register client inline:', err);
      toast.error('Could not register new client automatically');
      return null;
    }
  };

  // Validate context before allowing quote creation
  const validateContext = async () => {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user?.id) {
      throw new Error('User not authenticated');
    }

    const tenant = await tenantService.getCurrentTenant();
    if (!tenant?.id) {
      throw new Error('No tenant context - please refresh and try again');
    }

    if (!clientName.trim()) {
      throw new Error('Client name is required');
    }

    if (!projectName.trim()) {
      throw new Error('Project name is required');
    }

    if (!selectedProjectTypeId) {
      throw new Error('Project type is required');
    }

    return { user, tenant };
  };

  const executeCreateQuote = async () => {
    setIsSaving(true);
    try {
      // Validate context first
      const { user, tenant } = await validateContext();

      // If it's a new client mode or no client selected from list but name entered manually
      const finalClientId = selectedClientId;
      if (!finalClientId && clientName) {
        const newId = await handleCreateNewClientInline();
        if (!newId) {
          setIsSaving(false);
          return; // Error handled in handleCreateNewClientInline
        }
      }

      const roomSelections = Object.entries(scopeCounts)
        .filter(([_, count]) => count > 0)
        .map(([category, count]) => ({ type_value: category, count }));

      // Get the full client if picked from dropdown
      const selectedClient = clients.find(c => c.id === finalClientId);
      const selectedPt = industries.flatMap(i => i.project_types).find(pt => pt.id === selectedProjectTypeId);
      const projectTypeName = selectedPt?.name || 'Interior Project';

      // Build and validate payload
      const payload = {
        tenant_id: tenant.id,
        user_id: user.id,
        project_type_id: selectedProjectTypeId,
        client_info: {
          name: normalizeTextValue(clientName, 'name'),
          projectName: normalizeTextValue(projectName, 'project_name'),
          projectType: projectTypeName,
          bdmId: designConsultantId || null,
          email: selectedClient?.email || null,
          phone: selectedClient?.phone || null,
          address: selectedClient?.address || null,
          gstNumber: selectedClient?.gstNumber || null,
        },
        scope_selections: roomSelections,
      };

      // Validate with Zod schema
      const validatedPayload = validateQuoteCreation(payload);

      // Use tenantService to create via DB function (fn_create_quote_from_template)
      const result = await tenantService.createQuoteFromTemplate({
        projectTypeId: validatedPayload.project_type_id,
        clientInfo: validatedPayload.client_info,
        scopeSelections: validatedPayload.scope_selections || [],
      });

      await onCreateQuote(
        result.quote_id,
        designConsultantId,
      );

      resetForm();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create quotation:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize quotation workspace';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setClientName('');
    setProjectName('');
    setSelectedIndustryId('');
    setSelectedProjectTypeId('');
    setDesignConsultantId(designConsultants.length > 0 ? designConsultants[0].id : '');
    setScopeCounts({});
    setSelectedClientId('');
    setCurrentStep(1);
    setIsAddingConsultant(false);
    setNewConsultantForm({ name: '', email: '', phone: '' });
  };

  const updateScopeCount = (category: string, delta: number) => {
    setScopeCounts(prev => ({
      ...prev,
      [category]: Math.max(0, (prev[category] ?? 0) + delta)
    }));
  };

  const hasScopes = Object.values(scopeCounts).some(count => count > 0);
  const totalScopes = Object.values(scopeCounts).reduce((a, b) => a + b, 0);

  const nextStep = () => {
    if (currentStep < 4) setCurrentStep((prev) => (prev + 1) as WizardStep);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep((prev) => (prev - 1) as WizardStep);
  };

  const isStep1Valid = clientName.trim().length > 0 && projectName.trim().length > 0 && designConsultantId;
  const isStep2Valid = !!selectedProjectTypeId;
  const selectedPT   = industries.flatMap(i => i.project_types)
                         .find(pt => pt.id === selectedProjectTypeId);
  const selectedIndustry = industries.find(i => i.id === selectedIndustryId);
  const isStep3Valid = true; // Room allocation is optional — templates or custom rooms can be loaded in workspace
  const scopeStepCopy = getScopeStepCopy(selectedIndustry?.slug);
  const scopeStepLabel = scopeStepCopy.label;
  const scopeStepDescription = scopeStepCopy.description;

  const currentStepTitle =
    currentStep === 1
      ? 'Client'
      : currentStep === 2
        ? 'Industry'
        : currentStep === 3
          ? scopeStepLabel
          : 'Review';

  const renderStepIndicator = () => (
    <div className="mb-6 sm:mb-8">
      <div className="flex items-center justify-between px-2">
      {[1, 2, 3, 4].map((step) => (
        <div key={step} className="flex flex-col items-center relative flex-1">
          <div
            className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 z-10 border-2",
              currentStep === step
                ? "bg-primary border-primary text-foreground shadow-xl shadow-primary/20 scale-110"
                : currentStep > step
                  ? "bg-emerald-500 border-emerald-500 text-white"
                  : "bg-background border-muted text-muted-foreground"
            )}
          >
            {currentStep > step ? <Check className="h-4 w-4" /> : step}
          </div>
          <span className={cn(
            "text-[10px] uppercase tracking-widest font-medium mt-2 hidden sm:block",
            currentStep === step ? "text-primary" : "text-muted-foreground"
          )}>
            {step === 1 ? 'Client' : step === 2 ? 'Industry' : step === 3 ? scopeStepLabel : 'Review'}
          </span>
          {step < 4 && (
            <div className={cn(
              "absolute top-4 left-[50%] w-full h-[2px] -z-0",
              currentStep > step ? "bg-emerald-500" : "bg-muted"
            )} style={{ width: step === 4 ? 0 : '100%' }} />
          )}
        </div>
      ))}
      </div>
      <div className="mt-4 flex items-center justify-between rounded-xl border border-border bg-muted/30 px-3 py-2 sm:hidden">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          Step {currentStep} of 4
        </span>
        <span className="text-xs font-semibold uppercase tracking-widest text-primary">
          {currentStepTitle}
        </span>
      </div>
    </div>
  );

  const consultantName = designConsultants.find(c => c.id === designConsultantId)?.name || 'N/A';
  const estimateTypeLabel = selectedPT?.name ?? 'N/A';

  return (
    <Dialog open={open} onOpenChange={(val) => {
      if (!val) resetForm();
      onOpenChange(val);
    }}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:max-w-2xl h-[100dvh] sm:h-auto max-h-[100dvh] sm:max-h-[90vh] overflow-hidden bg-background border border-border p-0 flex flex-col gap-0 rounded-none sm:rounded-[32px]">
        <div className="p-10 sm:p-12 border-b border-border bg-muted/50 shrink-0 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-emerald-600/5 blur-[80px] rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
          <DialogHeader>
            <DialogTitle className="flex items-center gap-5 text-xl sm:text-3xl font-semibold tracking-tighter text-foreground uppercase pr-8 font-display">
              <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-2xl bg-emerald-600 flex items-center justify-center shadow-2xl shadow-emerald-500/20 shrink-0 border border-border">
                <FilePlus className="h-7 w-7 text-foreground" />
              </div>
              New Quotation Wizard
            </DialogTitle>
            <DialogDescription className="font-medium text-muted-foreground text-sm uppercase tracking-widest mt-2 opacity-60">
              Configure client, project type, and scope.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto px-10 py-10 sm:px-12 sm:py-10">
          {renderStepIndicator()}

          {/* STEP 1: CLIENT INFORMATION */}
          {currentStep === 1 && (
            <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-500">
              <div className="space-y-8">
                <div className="space-y-3">
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-500">Agency Identification</Label>
                  <Popover open={openClientCombo} onOpenChange={setOpenClientCombo}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={openClientCombo}
                        className={cn(
                          "w-full justify-between h-14 px-5 text-sm font-semibold rounded-2xl border-border bg-muted/50 hover:bg-muted/50 transition-all text-foreground uppercase tracking-tight",
                          !clientName && "text-muted-foreground"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <User className="h-4 w-4 text-emerald-500" />
                          {clientName || "Search or register client..."}
                        </div>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-40" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[min(var(--radix-popover-trigger-width),calc(100vw-2rem))] p-2 bg-[#0a0a0c] rounded-2xl shadow-3xl overflow-hidden border-border" align="start">
                      <Command className="bg-transparent">
                        <CommandInput placeholder="Filtering agency registry..." className="h-12 border-none font-semibold uppercase text-xs" onValueChange={(val) => {
                          setClientName(val);
                          if (!val) setSelectedClientId('');
                        }} />
                        <CommandEmpty className="py-10 text-center">
                          <p className="text-[10px] font-semibold text-slate-600 uppercase tracking-widest">No Client Record Found</p>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="mt-4 font-semibold rounded-xl bg-emerald-600/10 text-emerald-500 border border-emerald-500/20 px-6 py-4 uppercase text-[10px] tracking-widest h-10 hover:bg-emerald-600 hover:text-white transition-all"
                            onClick={() => setOpenClientCombo(false)}
                          >
                            Register "{clientName}"
                          </Button>
                        </CommandEmpty>
                        <CommandGroup className="max-h-[250px] overflow-y-auto p-2">
                          {clients.map((client) => (
                            <CommandItem
                              key={client.id}
                              value={client.name}
                              onSelect={() => handleClientSelect(client.id)}
                              className="px-4 py-4 cursor-pointer hover:bg-muted/50 rounded-xl font-semibold uppercase text-[10px] tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <div className="flex items-center justify-between w-full">
                                <div className="flex flex-col gap-1">
                                  <span>{client.name}</span>
                                  {client.projectName && <span className="text-[8px] font-medium text-slate-600 tracking-wider">PROJECT: {client.projectName.toUpperCase()}</span>}
                                </div>
                                <Check className={cn("h-4 w-4 text-emerald-500", selectedClientId === client.id ? "opacity-100" : "opacity-0")} />
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-3">
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-500">Node Identifier / Site</Label>
                  <div className="relative group">
                    <Building2 className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-500/40 transition-colors group-focus-within:text-emerald-500" />
                    <Input
                      required
                      placeholder="e.g. SKYLINE PLAZA — SUITE 802"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      className="pl-14 h-14 font-semibold text-sm rounded-2xl border-border bg-muted/50 text-foreground uppercase tracking-tight focus:ring-emerald-500/10 placeholder:text-slate-800"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-500">Operation Lead</Label>
                    {!isAddingConsultant && (
                      <button
                        type="button"
                        onClick={() => setIsAddingConsultant(true)}
                        className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500 hover:text-emerald-400 transition-colors"
                      >
                        + Register New Lead
                      </button>
                    )}
                  </div>

                  {isAddingConsultant ? (
                    <div className="p-6 rounded-2xl border border-border bg-slate-900/30 space-y-4 animate-in fade-in duration-300">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Full Name *</Label>
                          <Input
                            required
                            placeholder="e.g. John Doe"
                            value={newConsultantForm.name}
                            onChange={(e) => setNewConsultantForm(prev => ({ ...prev, name: e.target.value }))}
                            className="h-10 font-semibold text-xs rounded-xl border-border bg-muted/50 text-foreground uppercase tracking-tight"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Email</Label>
                          <Input
                            type="email"
                            placeholder="e.g. john@dcode.com"
                            value={newConsultantForm.email}
                            onChange={(e) => setNewConsultantForm(prev => ({ ...prev, email: e.target.value }))}
                            className="h-10 font-semibold text-xs rounded-xl border-border bg-muted/50 text-foreground uppercase tracking-tight"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Phone</Label>
                          <Input
                            placeholder="e.g. +919633860898"
                            value={newConsultantForm.phone}
                            onChange={(e) => setNewConsultantForm(prev => ({ ...prev, phone: e.target.value }))}
                            className="h-10 font-semibold text-xs rounded-xl border-border bg-muted/50 text-foreground uppercase tracking-tight"
                          />
                        </div>
                        <div className="flex items-end gap-2 justify-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setIsAddingConsultant(false);
                              setNewConsultantForm({ name: '', email: '', phone: '' });
                            }}
                            className="font-semibold uppercase tracking-widest text-[9px] h-10 px-4 rounded-xl border-zinc-200 dark:border-white/10"
                            disabled={isSavingConsultant}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleCreateNewConsultantInline}
                            className="font-semibold uppercase tracking-widest text-[9px] h-10 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-black shadow-lg"
                            disabled={isSavingConsultant}
                          >
                            {isSavingConsultant ? 'Saving...' : 'Save Lead'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Select value={designConsultantId} onValueChange={setDesignConsultantId}>
                      <SelectTrigger className="h-14 font-semibold rounded-2xl border-border bg-muted/50 text-foreground uppercase tracking-tight">
                        <div className="flex items-center gap-3">
                          <Users className="h-4 w-4 text-emerald-500" />
                          <SelectValue placeholder="Lead Consultant" />
                        </div>
                      </SelectTrigger>
                      <SelectContent className="rounded-2xl bg-[#0a0a0c] border-border shadow-3xl p-2">
                        {designConsultants.map((dc) => (
                          <SelectItem key={dc.id} value={dc.id} className="font-semibold text-[10px] uppercase tracking-widest py-3 rounded-xl hover:bg-muted/50 transition-colors cursor-pointer">{dc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: INDUSTRY & PROJECT TYPE */}
          {currentStep === 2 && (
            <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-500">
              <div className="bg-emerald-600/5 p-8 rounded-[24px] border border-emerald-600/10 flex items-start gap-6 mb-2 group">
                <div className="h-14 w-14 rounded-2xl bg-emerald-600/10 border border-emerald-500/20 flex items-center justify-center shrink-0 group-hover:bg-emerald-600 group-hover:text-black transition-all">
                  <Layout className="h-7 w-7 text-emerald-500 group-hover:text-black transition-colors" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground uppercase tracking-tight text-lg">Industry Architecture</h3>
                  <p className="text-sm text-muted-foreground font-medium uppercase tracking-widest text-[9px] mt-1 opacity-60">Defining structural nodes and financial protocols.</p>
                </div>
              </div>

              {industries.length === 0 ? (
                <div className="p-10 rounded-[24px] border border-dashed border-border text-center space-y-3">
                  <p className="text-[10px] font-semibold text-slate-700 uppercase tracking-[0.4em]">No Industries Available Yet</p>
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-[0.2em]">
                    Finish subscriber onboarding in Settings and save at least one industry before creating an estimate.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-10">
                  {/* Industry picker */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {industries.map(ind => (
                      <button
                        key={ind.id}
                        type="button"
                        onClick={() => {
                          setSelectedIndustryId(ind.id);
                          setSelectedProjectTypeId(ind.project_types[0]?.id ?? '');
                          setScopeCounts({});
                        }}
                        className={cn(
                          "flex flex-col items-center gap-4 p-6 rounded-[24px] border-2 transition-all text-center group/btn",
                          selectedIndustryId === ind.id
                            ? "bg-emerald-600/10 border-emerald-600 shadow-2xl shadow-emerald-600/10"
                            : "bg-muted/50 border-border hover:border-emerald-500/30"
                        )}
                      >
                        <div className={cn(
                          "h-10 w-10 rounded-xl flex items-center justify-center text-lg transition-transform duration-500 group-hover/btn:scale-110",
                          selectedIndustryId === ind.id ? "bg-emerald-600 text-black" : "bg-muted/50 text-slate-600"
                        )}>
                          {selectedIndustryId === ind.id
                            ? <Check className="h-5 w-5" />
                            : <Sparkles className="h-5 w-5" />}
                        </div>
                        <span className="text-[10px] font-semibold uppercase tracking-widest leading-tight text-foreground group-hover/btn:text-emerald-500 transition-colors">
                          {ind.name}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Project type picker — shown after industry selected */}
                  {selectedIndustryId && (() => {
                    const ind = industries.find(i => i.id === selectedIndustryId);
                    return ind ? (
                      <div className="grid grid-cols-1 gap-4">
                        <Label className="text-[9px] font-semibold uppercase tracking-[0.4em] text-slate-600 mb-2">
                          Project Classification — NODE {ind.name.toUpperCase()}
                        </Label>
                        {ind.project_types.map(pt => (
                          <button
                            key={pt.id}
                            type="button"
                            onClick={() => {
                              setSelectedProjectTypeId(pt.id);
                              setScopeCounts({});
                              industryService.getProjectTypeWithTemplate(pt.id).then(full => {
                                if (full) {
                                  setScopeGroupTypes(
                                    full.scope_group_definitions.map(d => ({
                                      value:          d.value,
                                      label:          d.label,
                                      allowMultiple: d.allow_multiple,
                                    }))
                                  );
                                }
                              });
                            }}
                            className={cn(
                              "flex items-center justify-between p-6 rounded-2xl border transition-all text-left group/pt",
                              selectedProjectTypeId === pt.id
                                ? "bg-emerald-600/5 border-emerald-600 shadow-xl"
                                : "bg-muted/50 border-border hover:bg-muted/50 hover:border-border"
                            )}
                          >
                            <div>
                              <p className="font-semibold text-foreground text-sm uppercase tracking-tight group-hover/pt:text-emerald-400 transition-colors">{pt.name}</p>
                              {pt.description && (
                                <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider mt-1 opacity-60">{pt.description}</p>
                              )}
                            </div>
                            <div className={cn(
                                "h-8 w-8 rounded-xl flex items-center justify-center shrink-0 ml-4 transition-all duration-500",
                                selectedProjectTypeId === pt.id ? "bg-emerald-600 text-black scale-100" : "bg-muted/50 text-slate-800 scale-90"
                            )}>
                                <Check className="h-4 w-4" />
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          )}

          {/* STEP 3: SCOPE REQUIREMENTS */}
          {currentStep === 3 && (
            <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-500">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-500">{scopeStepDescription.toUpperCase()}</Label>
                <div className="px-5 py-2 bg-emerald-600/10 border border-emerald-500/20 rounded-full shadow-lg shadow-emerald-500/5 animate-pulse">
                  <span className="text-[10px] font-semibold text-emerald-500 uppercase tracking-widest">{totalScopes} NODES ALLOCATED</span>
                </div>
              </div>

              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-2xl flex items-center justify-between text-xs text-amber-600 dark:text-amber-400 font-medium">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-500 shrink-0" />
                  <span>Planning to use a Template? You can skip room allocation here and click <strong>&quot;Advance Node&quot;</strong> to load your template in 1 click!</span>
                </div>
              </div>

              {scopeGroupTypes.length === 0 ? (
                <div className="p-10 rounded-[32px] border border-dashed border-border text-center">
                  <p className="text-[10px] font-semibold text-slate-800 uppercase tracking-[0.4em]">No Scope Definitions Established</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {scopeGroupTypes.map(option => (
                    <div
                      key={option.value}
                      className={cn(
                        "flex items-center justify-between p-6 rounded-[24px] border transition-all duration-500",
                        (scopeCounts[option.value] ?? 0) > 0
                          ? "bg-emerald-600/5 border-emerald-600 shadow-xl"
                          : "bg-muted/50 border-border hover:bg-muted/50"
                      )}
                    >
                      <div className="flex flex-col gap-2">
                        <span className="text-[9px] font-semibold uppercase tracking-[0.3em] text-slate-600 transition-colors group-hover:text-emerald-500">
                            {option.label}
                        </span>
                        <span className="text-3xl font-semibold text-foreground font-display">{(scopeCounts[option.value] ?? 0).toString().padStart(2, '0')}</span>
                      </div>
                      <div className="flex items-center gap-3 bg-muted/40 p-2 rounded-2xl border border-border shadow-inner">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-10 w-10 rounded-xl hover:bg-muted/50 hover:text-foreground transition-all text-slate-600"
                          onClick={() => updateScopeCount(option.value, -1)}
                          disabled={(scopeCounts[option.value] ?? 0) === 0 || (!option.allowMultiple && (scopeCounts[option.value] ?? 0) <= 1)}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <div className="h-4 w-px bg-muted/50" />
                        <Button
                          type="button"
                          variant="secondary"
                          size="icon"
                          className="h-10 w-10 rounded-xl bg-emerald-600 text-black hover:bg-emerald-500 shadow-2xl shadow-emerald-500/20 transition-all active:scale-95"
                          onClick={() => updateScopeCount(option.value, 1)}
                          disabled={!option.allowMultiple && (scopeCounts[option.value] ?? 0) >= 1}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP 4: REVIEW & CONFIRM */}
          {currentStep === 4 && (
            <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-500">
              <div className="bg-[#050505]/60 p-10 sm:p-12 rounded-[40px] border border-border relative overflow-hidden group shadow-3xl">
                <div className="absolute top-0 right-0 p-10 opacity-5 pointer-events-none group-hover:opacity-10 transition-opacity duration-1000">
                  <ClipboardCheck className="h-48 w-48 text-emerald-500" />
                </div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-600/5 blur-[100px] rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />

                <h3 className="text-2xl font-semibold text-foreground uppercase tracking-tighter mb-10 font-display flex items-center gap-3">
                    <div className="h-2 w-2 rounded-full bg-emerald-500" />
                    Archive Verification
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-10 gap-x-12">
                  <div className="space-y-2">
                    <p className="text-[9px] font-semibold text-slate-700 uppercase tracking-[0.4em]">Node Agency</p>
                    <p className="font-semibold text-foreground truncate text-lg tracking-tight uppercase">{clientName}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[9px] font-semibold text-slate-700 uppercase tracking-[0.4em]">Operations Lead</p>
                    <p className="font-semibold text-foreground truncate text-lg tracking-tight uppercase">{consultantName}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[9px] font-semibold text-slate-700 uppercase tracking-[0.4em]">Site Vector</p>
                    <p className="font-semibold text-foreground truncate text-lg tracking-tight uppercase">{projectName}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[9px] font-semibold text-slate-700 uppercase tracking-[0.4em]">Node Protocol</p>
                    <p className="font-semibold text-emerald-500 truncate text-lg tracking-tight uppercase">{estimateTypeLabel}</p>
                  </div>
                </div>

                <div className="mt-12 pt-10 border-t border-border">
                  <p className="text-[9px] font-semibold text-slate-700 uppercase tracking-[0.4em] mb-6">Allocated Logical Units</p>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(scopeCounts)
                      .filter(([_, count]) => count > 0)
                      .map(([category, count]) => {
                        const label = scopeGroupTypes.find(s => s.value === category)?.label ?? category;
                        return (
                          <div key={category} className="px-5 py-2.5 bg-muted/50 rounded-2xl border border-border flex items-center gap-3 hover:bg-emerald-600/10 hover:border-emerald-500/20 transition-all group/unit">
                            <span className="text-[9px] font-semibold text-emerald-500 group-hover/unit:scale-110 transition-transform">{count.toString().padStart(2, '0')}</span>
                            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest group-hover/unit:text-foreground">
                                {label}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>

              <div className="p-6 bg-amber-500/5 border border-amber-500/20 rounded-[24px] flex gap-4 shadow-xl shadow-amber-500/0 hover:shadow-amber-500/5 transition-all">
                <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                    <Sparkles className="h-5 w-5 text-amber-500" />
                </div>
                <p className="text-[10px] font-semibold text-amber-500/80 leading-relaxed uppercase tracking-[0.1em] mt-1">
                  Ready to deploy high-fidelity estimation matrices? Authorization will lock architectural defaults into the current workspace registry.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="p-10 sm:p-12 border-t border-border flex justify-between items-center gap-5 bg-muted/50 shrink-0 sticky bottom-0 z-20">
          <Button
            variant="ghost"
            onClick={currentStep === 1 ? () => onOpenChange(false) : prevStep}
            className="font-semibold uppercase tracking-[0.3em] text-[10px] sm:text-xs h-14 px-8 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-2xl transition-all"
          >
            {currentStep === 1 ? 'Abort' : 'Return'}
          </Button>

          {currentStep < 4 ? (
            <Button
              onClick={nextStep}
              disabled={
                (currentStep === 1 && !isStep1Valid) ||
                (currentStep === 2 && !isStep2Valid) ||
                (currentStep === 3 && !isStep3Valid)
              }
              className="font-semibold uppercase tracking-[0.3em] text-[10px] sm:text-xs h-16 rounded-[24px] bg-white text-black hover:bg-slate-50 shadow-2xl shadow-white/5 px-10 group min-w-[180px] sm:min-w-0 transition-all active:scale-95 border-none"
            >
              Advance Node
              <ChevronRight className="ml-3 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          ) : (
            <Button
              onClick={executeCreateQuote}
              disabled={!isContextReady || isSaving}
              className="font-semibold uppercase tracking-[0.3em] text-[10px] sm:text-xs h-16 rounded-[24px] bg-emerald-600 hover:bg-emerald-500 text-black shadow-2xl shadow-emerald-500/20 px-10 min-w-[200px] sm:min-w-0 transition-all active:scale-95 border-none"
            >
              {isSaving ? (
                <div className="flex items-center gap-3">
                  <div className="h-5 w-5 border-3 border-black/20 border-t-black rounded-full animate-spin" />
                  Orchestrating...
                </div>
              ) : !isContextReady ? (
                <div className="flex items-center gap-3">
                  <div className="h-5 w-5 border-3 border-black/20 border-t-black rounded-full animate-spin" />
                  Loading...
                </div>
              ) : 'Deploy Workspace'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
